import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDialog } from '@angular/material/dialog';
import { AlertService } from '../../../_services/alert.service';
import { ContainerReceipt, DeliveryValidationService } from '../../../_services/delivery-validation.service';
import { ConfirmDialogComponent } from '../../../../Shared/_components/confirm-dialog/confirm-dialog.component';

export interface ContainerCard extends ContainerReceipt {
  // What actually came out of the container, per shipment line - starts at
  // what it should hold, so a clean container is one click.
  arrived: Record<number, number | null>;
  // Of what arrived, how many are damaged (written off as scrap), and why.
  damaged: Record<number, number | null>;
  damageNote: Record<number, string>;
  // Of what arrived, how many go back to the supplier, and why.
  returned: Record<number, number | null>;
  returnNote: Record<number, string>;
  saving: boolean;
}

// Wraps a delivered container for the form, keeping anything already typed
// on the previous copy of the same container.
export function toContainerCard(receipt: ContainerReceipt, previous?: ContainerCard): ContainerCard {
  return {
    ...receipt,
    arrived: previous?.arrived ?? Object.fromEntries(receipt.lines.map((l) => [l.id, l.expected])),
    damaged: previous?.damaged ?? {},
    damageNote: previous?.damageNote ?? {},
    returned: previous?.returned ?? {},
    returnNote: previous?.returnNote ?? {},
    saving: false,
  };
}

export type ContainerReceived = { units: number; damaged: number; returned: number; purchaseOrders: number };

// One delivered container being checked in: count what arrived (and what's
// damaged), then validate - PurchaseShipmentsService.receiveContainer books
// it into each PO it carries, writes damage off as scrap and marks it
// Received. Used on the Receipts list and on the container's own page.
@Component({
    selector: 'app-container-receipt-card',
    imports: [CommonModule, FormsModule, RouterLink, MatIconModule, MatTooltipModule, MatProgressSpinnerModule, DatePipe],
    templateUrl: './container-receipt-card.component.html'
})
export class ContainerReceiptCardComponent {
  @Input({ required: true }) card!: ContainerCard;
  // On the list, the header opens the container's own page.
  @Input() linkToDetail = true;
  @Output() received = new EventEmitter<ContainerReceived>();

  constructor(
    private deliveryValidationService: DeliveryValidationService,
    private alertService: AlertService,
    private dialog: MatDialog,
    private router: Router,
  ) {}

  openDetail(): void {
    if (this.linkToDetail) this.router.navigate(['/delivery-validation/receipts/container', this.card.id]);
  }

  expectedUnits(c: ContainerCard): number {
    return c.lines.reduce((n, l) => n + l.expected, 0);
  }

  arrivedUnits(c: ContainerCard): number {
    return c.lines.reduce((n, l) => n + (Number(c.arrived[l.id]) || 0), 0);
  }

  // Arrived minus expected for a line (negative = short).
  difference(c: ContainerCard, lineId: number, expected: number): number {
    return Math.round(((Number(c.arrived[lineId]) || 0) - expected) * 100) / 100;
  }

  discrepancies(c: ContainerCard): number {
    return c.lines.filter((l) => this.difference(c, l.id, l.expected) !== 0).length;
  }

  resetToExpected(c: ContainerCard): void {
    c.lines.forEach((l) => (c.arrived[l.id] = l.expected));
  }

  damagedUnits(c: ContainerCard): number {
    return c.lines.reduce((n, l) => n + (Number(c.damaged[l.id]) || 0), 0);
  }

  returnedUnits(c: ContainerCard): number {
    return c.lines.reduce((n, l) => n + (Number(c.returned[l.id]) || 0), 0);
  }

  // Damaged must be whole units, and damaged + returned can't be more than arrived.
  damageError(c: ContainerCard, lineId: number): string | null {
    const damaged = Number(c.damaged[lineId]) || 0;
    if (damaged < 0 || !Number.isInteger(damaged)) return 'Whole units';
    if (damaged + (Number(c.returned[lineId]) || 0) > (Number(c.arrived[lineId]) || 0)) return 'More than arrived';
    return null;
  }

  returnError(c: ContainerCard, lineId: number): string | null {
    const returned = Number(c.returned[lineId]) || 0;
    if (returned < 0) return 'Not below 0';
    if (returned + (Number(c.damaged[lineId]) || 0) > (Number(c.arrived[lineId]) || 0)) return 'More than arrived';
    return null;
  }

  // Into stock = arrived - damaged - returned.
  goodUnits(c: ContainerCard): number {
    return this.arrivedUnits(c) - this.damagedUnits(c) - this.returnedUnits(c);
  }

  // "2 damaged, 3 returned" for the validate button.
  setAsideLabel(c: ContainerCard): string {
    const parts: string[] = [];
    if (this.damagedUnits(c)) parts.push(`${this.damagedUnits(c)} damaged`);
    if (this.returnedUnits(c)) parts.push(`${this.returnedUnits(c)} returned`);
    return parts.join(', ');
  }

  hasDamageErrors(c: ContainerCard): boolean {
    return c.lines.some((l) => !!this.damageError(c, l.id) || !!this.returnError(c, l.id));
  }

  // The POs a container carries, for its header.
  containerOrders(c: ContainerCard): { reference: string; supplierName: string | null }[] {
    const seen = new Map<string, string | null>();
    c.lines.forEach((l) => seen.set(l.poReference, l.supplierName));
    return [...seen].map(([reference, supplierName]) => ({ reference, supplierName }));
  }

  validateContainer(c: ContainerCard): void {
    if (this.hasDamageErrors(c)) {
      this.alertService.alert("Check the damaged quantities - they can't be more than arrived", 'error');
      return;
    }
    const label = c.containerNo || `container on ${c.shipmentReference}`;
    const diffs = this.discrepancies(c);
    const damaged = this.damagedUnits(c);
    const returned = this.returnedUnits(c);
    if (!diffs && !damaged && !returned) {
      this.receiveContainer(c);
      return;
    }
    const good = this.goodUnits(c);
    const parts: string[] = [];
    if (diffs) parts.push(`${diffs} line${diffs === 1 ? " doesn't" : "s don't"} match what the container should hold (${this.arrivedUnits(c)} of ${this.expectedUnits(c)} units arrived).`);
    if (damaged) parts.push(`${damaged} damaged unit${damaged === 1 ? ' is' : 's are'} written off as scrap.`);
    if (returned) parts.push(`${returned} unit${returned === 1 ? ' goes' : 's go'} back to the supplier and stay${returned === 1 ? 's' : ''} outstanding on the PO.`);
    parts.push(`${good} good unit${good === 1 ? '' : 's'} go into ${c.store} stock.`);
    const notes: string[] = [];
    if (damaged) notes.push('Damaged units count as delivered on the PO and show in the scrap log.');
    if (returned) notes.push('Returns are noted on the PO for the supplier to replace or credit.');
    if (!notes.length) notes.push('Anything short stays outstanding on its purchase order.');
    const what = [damaged ? 'damage' : '', returned ? 'returns' : ''].filter(Boolean).join(' and ') || 'differences';
    this.dialog
      .open(ConfirmDialogComponent, {
        data: {
          title: `Receive ${label} with ${what}?`,
          message: parts.join(' '),
          detail: notes.join(' '),
          confirmText: 'Receive as entered',
          tone: 'warning',
        },
      })
      .afterClosed()
      .subscribe((result) => {
        if (result?.confirmation) this.receiveContainer(c);
      });
  }

  private receiveContainer(c: ContainerCard): void {
    const lines = c.lines.map((l) => ({
      lineId: l.id,
      quantity: Number(c.arrived[l.id]) || 0,
      damaged: Number(c.damaged[l.id]) || 0,
      damageNote: c.damageNote[l.id] || undefined,
      returned: Number(c.returned[l.id]) || 0,
      returnNote: c.returnNote[l.id] || undefined,
    }));
    c.saving = true;
    this.deliveryValidationService.receiveContainer(c.id, lines).subscribe({
      next: (result) => {
        c.saving = false;
        const good = result.units - result.damaged;
        this.alertService.alert(
          `${c.containerNo || 'Container'} received - ${good} unit${good === 1 ? '' : 's'} into ${c.store} stock` +
            (result.damaged ? `, ${result.damaged} damaged written off` : '') +
            (result.returned ? `, ${result.returned} returned to the supplier` : '') +
            (result.purchaseOrders > 1 ? ` across ${result.purchaseOrders} POs` : ''),
          'success',
        );
        this.received.emit({ units: result.units, damaged: result.damaged, returned: result.returned, purchaseOrders: result.purchaseOrders });
      },
      error: (error) => {
        c.saving = false;
        this.alertService.alert(error?.error?.message || 'Failed to receive the container', 'error');
      },
    });
  }
}
